/**
 * @file AgentCallCenter.jsx
 * @description The Fleet Agent's INTERNET CALL, inside this web application.
 *
 * The agent first messages you in the Chat page. If nobody replies within the
 * message wait, it rings you here: this component listens for the `agent_call`
 * socket event (and polls as a catch-up after a reload), shows a ringing
 * dialog, reads the question out loud when you answer, and posts your update
 * back to the agent — which closes the machine's follow-up. Decline or let it
 * ring out and the agent escalates to the management mail instead.
 *
 * Mounted once in MainLayout, so a call can reach you on any page.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import {
    Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, Stack, TextField, Typography,
} from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallIcon from '@mui/icons-material/Call';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import agentService from '../../services/agentService';

// Quick answers for the common site situations — one tap instead of typing.
const QUICK_ANSWERS = [
    'Machine is working',
    'Machine is idle today',
    'Under breakdown',
    'Shifted to another site',
    'Battery/power off',
    'Will check and update',
];

const POLL_MS = 45_000;

const AgentCallCenter = () => {
    const [call, setCall] = useState(null);          // the call being shown
    const [phase, setPhase] = useState('ringing');   // 'ringing' | 'answered'
    const [response, setResponse] = useState('');
    const [saving, setSaving] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(null);
    const socketRef = useRef(null);
    const callRef = useRef(null);

    callRef.current = call;

    const close = useCallback(() => {
        try { window.speechSynthesis?.cancel(); } catch (e) { /* not supported */ }
        setCall(null);
        setPhase('ringing');
        setResponse('');
        setSecondsLeft(null);
    }, []);

    // Only ever show one call at a time; a second one is picked up by the poll
    // once this one is dealt with.
    const offer = useCallback((incoming) => {
        if (!incoming || callRef.current) return;
        setCall(incoming);
        setPhase('ringing');
        setResponse('');
        setSecondsLeft(Math.round(incoming.ring_seconds || 120));
    }, []);

    // 1. Live: the agent pushes the call to this user's personal room.
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return undefined;
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';
        const socket = io(apiBaseUrl.replace(/\/api\/?$/, ''), {
            auth: { token },
            path: '/socket.io',
            transports: ['polling', 'websocket'],
        });
        socket.on('agent_call', offer);
        socketRef.current = socket;
        return () => {
            try { socket.off('agent_call', offer); socket.disconnect(); } catch (e) { /* ignore */ }
        };
    }, [offer]);

    // 2. Catch-up: a call raised while the tab was closed still rings on return.
    useEffect(() => {
        let alive = true;
        const check = async () => {
            try {
                const res = await agentService.getActiveCalls();
                if (!alive || !res?.success) return;
                const next = (res.data || [])[0];
                if (next) offer({ ...next, ring_seconds: res.ring_seconds });
            } catch (e) { /* offline / not signed in — try again next tick */ }
        };
        check();
        const t = setInterval(check, POLL_MS);
        return () => { alive = false; clearInterval(t); };
    }, [offer]);

    // 3. Ring window — when it runs out the server marks the call missed and the
    //    agent escalates, so the dialog simply steps aside.
    useEffect(() => {
        if (!call || phase !== 'ringing' || secondsLeft == null) return undefined;
        if (secondsLeft <= 0) { close(); return undefined; }
        const t = setTimeout(() => setSecondsLeft((s) => (s == null ? null : s - 1)), 1000);
        return () => clearTimeout(t);
    }, [call, phase, secondsLeft, close]);

    const handleAnswer = () => {
        setPhase('answered');
        setSecondsLeft(null);
        // Read the question out, the way a call would.
        try {
            const speech = new window.SpeechSynthesisUtterance(call.message);
            speech.lang = 'en-IN';
            speech.rate = 0.95;
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(speech);
        } catch (e) { /* no speech support — the text is on screen anyway */ }
    };

    const handleSubmit = async () => {
        if (!response.trim() || saving) return;
        setSaving(true);
        try {
            await agentService.answerCall(call.id, response.trim());
            close();
        } catch (e) {
            console.error('Failed to send the update to the Fleet Agent:', e);
        } finally {
            setSaving(false);
        }
    };

    const handleDecline = async () => {
        const id = call?.id;
        close();
        try { if (id) await agentService.declineCall(id); } catch (e) { /* server times it out anyway */ }
    };

    if (!call) return null;

    return (
        <Dialog open onClose={phase === 'ringing' ? handleDecline : undefined} maxWidth="xs" fullWidth>
            <DialogContent sx={{ pt: 3, pb: 1 }}>
                <Stack spacing={2} alignItems="center" textAlign="center">
                    <Avatar
                        sx={{
                            width: 64, height: 64, bgcolor: 'primary.main',
                            animation: phase === 'ringing' ? 'agentRing 1.4s ease-in-out infinite' : 'none',
                            '@keyframes agentRing': {
                                '0%, 100%': { boxShadow: '0 0 0 0 rgba(37,99,235,0.45)' },
                                '50%': { boxShadow: '0 0 0 14px rgba(37,99,235,0)' },
                            },
                        }}
                    >
                        <SmartToyIcon fontSize="large" />
                    </Avatar>

                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Fleet Agent</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {phase === 'ringing'
                                ? `Internet call · needs an update${call.equipment_id ? ` on ${call.equipment_id}` : ''}`
                                : `On call${call.equipment_id ? ` · ${call.equipment_id}` : ''}`}
                        </Typography>
                    </Box>

                    {phase === 'ringing' && (
                        <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`Ringing · ${Math.max(0, secondsLeft ?? 0)}s`}
                        />
                    )}

                    <Typography
                        variant="body2"
                        sx={{
                            whiteSpace: 'pre-line', textAlign: 'left', width: '100%',
                            bgcolor: 'action.hover', p: 1.5, borderRadius: 2,
                        }}
                    >
                        {call.message}
                    </Typography>

                    {phase === 'answered' && (
                        <>
                            <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="center">
                                {QUICK_ANSWERS.map((q) => (
                                    <Chip
                                        key={q}
                                        label={q}
                                        size="small"
                                        onClick={() => setResponse((r) => (r ? `${r}. ${q}` : q))}
                                        variant={response.includes(q) ? 'filled' : 'outlined'}
                                        color={response.includes(q) ? 'primary' : 'default'}
                                    />
                                ))}
                            </Stack>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                autoFocus
                                size="small"
                                label="What is happening with the machine?"
                                placeholder="e.g. Under breakdown since morning, hose replacement in progress"
                                value={response}
                                onChange={(e) => setResponse(e.target.value)}
                            />
                        </>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, justifyContent: 'center', gap: 1 }}>
                {phase === 'ringing' ? (
                    <>
                        <Button
                            onClick={handleDecline}
                            variant="outlined"
                            color="error"
                            startIcon={<CallEndIcon />}
                        >
                            Decline
                        </Button>
                        <Button
                            onClick={handleAnswer}
                            variant="contained"
                            color="success"
                            startIcon={<CallIcon />}
                        >
                            Answer
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={handleDecline} variant="outlined" color="inherit" disabled={saving}>
                            Hang up
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={!response.trim() || saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {saving ? 'Sending…' : 'Send update'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AgentCallCenter;
